import { Box, Typography, Container, Paper } from '@mui/material';
import React from 'react';
import { useEffect } from "react";
import { styled } from '@mui/material/styles';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SchoolIcon from '@mui/icons-material/School';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import StoreIcon from '@mui/icons-material/Store';
import StarBorderIcon from '@mui/icons-material/StarBorder';

const Accordion = styled((props) => (
    <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
    border: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: '8px !important',
    marginBottom: '16px',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    '&:hover': {
        boxShadow: '0 2px 8px rgba(220, 53, 69, 0.15)',
        transform: 'translateY(-1px)',
    },
    '&:not(:last-child)': {
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    },
    '&::before': {
        display: 'none',
    },
    '&.Mui-expanded': {
        margin: '0 0 16px 0',
    },
}));

const AccordionSummary = styled((props) => (
    <MuiAccordionSummary
        expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.85rem', color: "white" }} />}
        {...props}
    />
))(({ theme }) => ({
    background: "#343a40",
    color: "white",
    minHeight: '64px',
    padding: theme.spacing(0, 2),
    flexDirection: 'row-reverse',
    transition: 'all 0.3s ease',
    '&:hover': {
        background: "#212529",
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
        color: "white",
    },
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
        transform: 'rotate(90deg)',
        color: "#dc3545",
    },
    '& .MuiAccordionSummary-content': {
        marginLeft: theme.spacing(1.5),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
    },
    [theme.breakpoints.down('sm')]: {
        minHeight: '56px',
        padding: theme.spacing(0, 1.5),
        '& .MuiAccordionSummary-content': {
            gap: theme.spacing(1),
        },
    },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(3, 3, 3, 3),
    background: '#ffffff',
    borderTop: '1px solid rgba(0, 0, 0, 0.05)',
    lineHeight: 1.8,
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2.5, 2, 2.5, 2),
    },
}));


const About = () => {
    const lngsltd = JSON.parse(localStorage.getItem('languageSelected')) || {};

    useEffect(() => {
        window.scrollTo({
            top: 7,
            left: 7,
            behavior: "smooth"
        });
    }, []);

    return (
        <Box 
            className='fullHW' 
            sx={{
                background: 'linear-gradient(180deg, #f5f5f5 0%, #ffffff 100%)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Container 
                maxWidth="lg" 
                sx={{ 
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    py: { xs: 3, sm: 4, md: 6 },
                    px: { xs: 2, sm: 3, md: 4 }
                }}
            >
                {/* Header Section */}
                <Box
                    sx={{
                        mb: 4,
                        pb: 2,
                        borderBottom: '2px solid #e0e0e0',
                    }}
                >
                    <Typography 
                        variant="h4" 
                        fontWeight={700}
                        sx={{
                            fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' },
                            color: '#212529',
                            mb: 0.5,
                            letterSpacing: '-0.02em',
                        }}
                    >
                        {lngsltd["About Ellinor Education"]}
                    </Typography>
                    <Typography 
                        variant="body2"
                        sx={{
                            color: '#6c757d',
                            fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                            fontWeight: 400,
                            mt: 0.5,
                        }}
                    >
                        {lngsltd["Your platform for learning and development"]}
                    </Typography>
                </Box>

                {/* Accordion Section */}
                <Box component="div" display="flex" flexDirection="column">
                    {/* Accordion 1: About Ellinor Education */}
                    <Accordion defaultExpanded>
                        <AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
                            <InfoOutlinedIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                            <Typography 
                                variant="subtitle1" 
                                fontWeight={600}
                                sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' } }}
                            >
                                {lngsltd["About Ellinor Education"]}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography 
                                variant="h6" 
                                fontWeight={600}
                                sx={{ 
                                    color: '#212529', 
                                    fontSize: { xs: '1rem', sm: '1.125rem' },
                                    mb: 2
                                }}
                            >
                                {lngsltd["Learning at Skeidar – shaped by our history, driven by our people"]}
                            </Typography>
                            <Typography sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' }, mb: 2 }}>
                                {lngsltd["Since 1912, Skeidar has helped Norwegians create good lives at home. What began as a small cabinetmaker's workshop in Ski has grown into one of Norway's most recognised furniture and interior specialists. For more than a century, Skeidar has combined craftsmanship, quality, and design with a deep understanding of how people live – at home, at the cabin, and in everyday life."]}
                            </Typography>
                            <Typography sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' }, mb: 2 }}>
                                {lngsltd["Today, Skeidar offers a wide range of modern quality furniture and interior solutions, supported by a strong portfolio of own-designed products and brands. At the same time, we work closely with several of the Nordic region's leading furniture and mattress brands, ensuring customers can choose the best in comfort, functionality, and design."]}
                            </Typography>
                            <Typography sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' }, mb: 2 }}>
                                {lngsltd["Every day, thousands of customers meet Skeidar through our stores and services. They come to us for guidance, inspiration, and reliable solutions – and they expect us to be helpful, professional, and available."]}
                            </Typography>
                            <Typography sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 500 }}>
                                {lngsltd["Ellinor Education exists to support exactly this."]}
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                    
                    {/* Accordion 2: What learning means to Skeidar */}
                    <Accordion>
                        <AccordionSummary aria-controls="panel2d-content" id="panel2d-header">
                            <SchoolIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                            <Typography 
                                variant="subtitle1" 
                                fontWeight={600}
                                sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' } }}
                            >
                                {lngsltd["What learning means to Skeidar"]}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' }, mb: 2 }}>
                                {lngsltd["At Skeidar, learning is closely connected to our role as a specialist retailer. Our expertise is not only found in the products we sell, but in the people who meet our customers – in how we listen, advise, and help them make the right choices for their homes."]}
                            </Typography>
                            <Typography 
                                variant="subtitle2" 
                                fontWeight={600}
                                sx={{ 
                                    color: '#212529', 
                                    fontSize: { xs: '0.9375rem', sm: '1rem' },
                                    mb: 1.5,
                                    mt: 2
                                }}
                            >
                                {lngsltd["Learning at Skeidar means:"]}
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, mb: 2, color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                <li style={{ marginBottom: '8px' }}>
                                    <Typography component="span" sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                        {lngsltd["Having strong product knowledge – from materials and design to comfort and durability"]}
                                    </Typography>
                                </li>
                                <li style={{ marginBottom: '8px' }}>
                                    <Typography component="span" sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                        {lngsltd["Staying curious about trends, customer needs, and better solutions"]}
                                    </Typography>
                                </li>
                                <li style={{ marginBottom: '8px' }}>
                                    <Typography component="span" sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                        {lngsltd["Developing skills that build trust, quality, and long-term relationships"]}
                                    </Typography>
                                </li>
                                <li style={{ marginBottom: '8px' }}>
                                    <Typography component="span" sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                        {lngsltd["Growing together – as colleagues, teams, and stores"]}
                                    </Typography>
                                </li>
                            </Box>
                            <Typography sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 500 }}>
                                {lngsltd["Ellinor Education brings this learning culture into everyday practice."]}
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                    
                    {/* Accordion 3: Why Ellinor Education exists */}
                    <Accordion>
                        <AccordionSummary aria-controls="panel3d-content" id="panel3d-header">
                            <QuestionAnswerIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                            <Typography 
                                variant="subtitle1" 
                                fontWeight={600}
                                sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' } }}
                            >
                                {lngsltd["Why Ellinor Education exists"]}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' }, mb: 2 }}>
                                {lngsltd["As Skeidar continues to grow and evolve, learning must be accessible, relevant, and consistent across roles and locations. Ellinor Education is Skeidar's in-house learning platform, created to ensure that knowledge, standards, and values are shared – and lived – throughout the organisation."]}
                            </Typography>
                            <Typography 
                                variant="subtitle2" 
                                fontWeight={600}
                                sx={{ 
                                    color: '#212529', 
                                    fontSize: { xs: '0.9375rem', sm: '1rem' },
                                    mb: 1.5,
                                    mt: 2
                                }}
                            >
                                {lngsltd["The platform supports:"]}
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, mb: 2, color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                <li style={{ marginBottom: '8px' }}>
                                    <Typography component="span" sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                        {lngsltd["New employees getting a confident and welcoming start at Skeidar"]}
                                    </Typography>
                                </li>
                                <li style={{ marginBottom: '8px' }}>
                                    <Typography component="span" sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                        {lngsltd["Experienced colleagues strengthening expertise and confidence in their roles"]}
                                    </Typography>
                                </li>
                                <li style={{ marginBottom: '8px' }}>
                                    <Typography component="span" sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                        {lngsltd["Leaders developing people, performance, and culture"]}
                                    </Typography>
                                </li>
                                <li style={{ marginBottom: '8px' }}>
                                    <Typography component="span" sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                        {lngsltd["Stores adapting learning to real customer situations and local needs"]}
                                    </Typography>
                                </li>
                            </Box>
                            <Typography sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 500 }}>
                                {lngsltd["Learning is not a one-time event. With Ellinor Education, learning becomes a continuous journey."]}
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                    
                    {/* Accordion 4: Supporting our stores */}
                    <Accordion>
                        <AccordionSummary aria-controls="panel4d-content" id="panel4d-header">
                            <StoreIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                            <Typography 
                                variant="subtitle1" 
                                fontWeight={600}
                                sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' } }}
                            >
                                {lngsltd["Supporting our stores – where Skeidar meets the customer"]}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' }, mb: 2 }}>
                                {lngsltd["Ellinor Education is designed with store reality in mind. Training is practical, flexible, and easy to access – enabling learning before, during, and after customer interactions."]}
                            </Typography>
                            <Typography 
                                variant="subtitle2" 
                                fontWeight={600}
                                sx={{ 
                                    color: '#212529', 
                                    fontSize: { xs: '0.9375rem', sm: '1rem' },
                                    mb: 1.5,
                                    mt: 2
                                }}
                            >
                                {lngsltd["For stores, this means:"]}
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, mb: 2, color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                <li style={{ marginBottom: '8px' }}>
                                    <Typography component="span" sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                        {lngsltd["Faster and stronger onboarding for new colleagues"]}
                                    </Typography>
                                </li>
                                <li style={{ marginBottom: '8px' }}>
                                    <Typography component="span" sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                        {lngsltd["Consistent and confident product and service knowledge across teams"]}
                                    </Typography>
                                </li>
                                <li style={{ marginBottom: '8px' }}>
                                    <Typography component="span" sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                        {lngsltd["More confident customer dialogue and higher quality sales"]}
                                    </Typography>
                                </li>
                                <li style={{ marginBottom: '8px' }}>
                                    <Typography component="span" sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                        {lngsltd["A shared understanding of service, professionalism, and care"]}
                                    </Typography>
                                </li>
                            </Box>
                            <Typography sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 500 }}>
                                {lngsltd["When learning feels relevant, it strengthens both performance and pride."]}
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                    
                    {/* Accordion 5: Rooted in design, quality, and care */}
                    <Accordion>
                        <AccordionSummary aria-controls="panel5d-content" id="panel5d-header">
                            <StarBorderIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                            <Typography 
                                variant="subtitle1" 
                                fontWeight={600}
                                sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' } }}
                            >
                                {lngsltd["Rooted in design, quality, and care"]}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' }, mb: 2 }}>
                                {lngsltd["Skeidar designs for better homes. We set high standards across the entire value chain – from design and material choices to production and how furniture works in real homes. The same care guides how we design learning."]}
                            </Typography>
                            <Typography 
                                variant="subtitle2" 
                                fontWeight={600}
                                sx={{ 
                                    color: '#212529', 
                                    fontSize: { xs: '0.9375rem', sm: '1rem' },
                                    mb: 1.5,
                                    mt: 2
                                }}
                            >
                                {lngsltd["Ellinor Education reflects Skeidar's core values:"]}
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, mb: 2, color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                <li style={{ marginBottom: '8px' }}>
                                    <Typography component="span" sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                        <strong>{lngsltd["Quality"]}</strong> {lngsltd["in content and competence"]}
                                    </Typography>
                                </li>
                                <li style={{ marginBottom: '8px' }}>
                                    <Typography component="span" sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                        <strong>{lngsltd["Care"]}</strong> {lngsltd["for customers, colleagues, and craftsmanship"]}
                                    </Typography>
                                </li>
                                <li style={{ marginBottom: '8px' }}>
                                    <Typography component="span" sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                        <strong>{lngsltd["Professionalism"]}</strong> {lngsltd["in how we work and develop"]}
                                    </Typography>
                                </li>
                                <li style={{ marginBottom: '8px' }}>
                                    <Typography component="span" sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                        <strong>{lngsltd["Togetherness"]}</strong> {lngsltd["– learning is a shared responsibility"]}
                                    </Typography>
                                </li>
                            </Box>
                            <Typography sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' }, mb: 2 }}>
                                {lngsltd["Because when our people grow, Skeidar grows. And when Skeidar grows, we help even more people feel at home."]}
                            </Typography>
                            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
                                <Typography 
                                    variant="h6" 
                                    fontWeight={700}
                                    sx={{ 
                                        color: '#212529', 
                                        fontSize: { xs: '1.125rem', sm: '1.25rem' },
                                        mb: 1
                                    }}
                                >
                                    {lngsltd["Ellinor Education"]}
                                </Typography>
                                <Typography sx={{ color: '#424242', fontSize: { xs: '0.875rem', sm: '1rem' }, fontStyle: 'italic' }}>
                                    {lngsltd["Learning that strengthens our people – and brings Skeidar's values to life, every day."]}
                                </Typography>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Box>
            </Container>
        </Box>
    );
};

export default About;